import {
  DemoIndicatorColoredIcon,
  PresentationsIndicatorColoredIcon,
  RecordingIndicatorColoredIcon,
  TourIndicatorColoredIcon,
} from '@carboncopyco/icons';
import type {
  CreatorFilter,
  RecordingTypeFilter,
  Sort,
  TypeFilter,
} from '@carboncopyco/types';
import type { CapsuleDropdownItem } from '@carboncopyco/ui';

export const EMAIL_DOMAIN_REGEXP =
  /^[a-zA-Z0-9]+([-.][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;

export const SPEAKER_NOTES_MAX_LENGTH = 100000;

export enum ValidationErrorMessages {
  required = 'Required field',
  contain40 = 'Can contain up to 40 characters',
  contain80 = 'Can contain up to 80 characters',
  contain200 = 'Can contain up to 200 characters',
  contain1000 = 'Can contain up to 1000 characters',
  containSimpleChars = 'Please use only letters, numbers and underscores',
  variableNameExists = 'Variable name already exists',
  startWithLetter = 'Variables should start with a letter',
  variableCodeAlreadyExists = 'Code already exists',
  invalidEmailDomain = 'Please enter a valid email domain',
}

export const creatorFilterOptions: CapsuleDropdownItem<CreatorFilter>[] = [
  { label: 'Created by anyone', id: 'all' },
  { label: 'My work', id: 'mineOnly' },
];

export const opportunitiesFilterOptions: CapsuleDropdownItem<CreatorFilter>[] =
  [
    {
      label: 'All opportunities',
      id: 'all',
      icon: <RecordingIndicatorColoredIcon />,
    },
    {
      label: 'My opportunities',
      id: 'mineOnly',
      icon: <RecordingIndicatorColoredIcon />,
    },
  ];

export const typeFilterOptions: CapsuleDropdownItem<TypeFilter>[] = [
  { label: 'All types', id: 'all' },
  {
    label: 'Clones',
    id: 'record',
    icon: <RecordingIndicatorColoredIcon />,
  },
  {
    label: 'Demos',
    id: 'demo',
    icon: <DemoIndicatorColoredIcon />,
  },
  {
    label: 'Presentations',
    id: 'presentation',
    icon: <PresentationsIndicatorColoredIcon />,
  },
  {
    label: 'Tours',
    id: 'tour',
    icon: <TourIndicatorColoredIcon />,
  },
];

export const sortOptions: CapsuleDropdownItem<Sort>[] = [
  { label: 'A --> Z', id: 'alphabet' },
  { label: 'Last updated', id: 'last_updated' },
];

export const demoTypeFilterOptions: CapsuleDropdownItem<RecordingTypeFilter>[] =
  [
    { label: 'All', id: 'all' },
    { label: 'Clone-based', id: 'dynamic_recording' },
    { label: 'Overlay', id: 'live_demo' },
  ];

export const planToPermission = {
  live: {
    presenter: {
      demo: {
        createADemo: false,
        shareDemo: false,
        updateShareApproval: false,
      },
      record: {
        getRecordings: false,
        getRecordingById: false,
        filterClone: false,
        createRecording: false,
      },
      tour: {
        createTour: false,
      },
      share: {
        createShareLink: false,
        getAllActiveShares: false,
        getAllSharesOfEntity: false,
        getFilterOptionsForSharesBySource: false,
        getShareDataBySessionToken: false,
        getShareMetadataByToken: false,
        getSharedEntityDataByToken: false,
        searchAllSharesBySource: false,
        sendVerificationEmailForShareInvitedUser: false,
        toggleShareActiveState: false,
        updateShareLink: false,
      },
    },
    editor: {
      demo: {
        createADemo: false,
        shareDemo: false,
        updateShareApproval: false,
      },
      record: {
        getRecordings: false,
        getRecordingById: false,
        filterClone: false,
        createRecording: false,
      },
      tour: {
        createTour: false,
      },
      share: {
        createShareLink: false,
        getAllActiveShares: false,
        getAllSharesOfEntity: false,
        getFilterOptionsForSharesBySource: false,
        getShareDataBySessionToken: false,
        getShareMetadataByToken: false,
        getSharedEntityDataByToken: false,
        searchAllSharesBySource: false,
        sendVerificationEmailForShareInvitedUser: false,
        toggleShareActiveState: false,
        updateShareLink: false,
      },
    },
    owner: {
      demo: {
        createADemo: false,
        shareDemo: false,
        updateShareApproval: false,
      },
      record: {
        getRecordings: false,
        getRecordingById: false,
        filterClone: false,
        createRecording: false,
      },
      tour: {
        createTour: false,
      },
      share: {
        createShareLink: false,
        getAllActiveShares: false,
        getAllSharesOfEntity: false,
        getFilterOptionsForSharesBySource: false,
        getShareDataBySessionToken: false,
        getShareMetadataByToken: false,
        getSharedEntityDataByToken: false,
        searchAllSharesBySource: false,
        sendVerificationEmailForShareInvitedUser: false,
        toggleShareActiveState: false,
        updateShareLink: false,
      },
    },
    admin: {
      demo: {
        createADemo: false,
        shareDemo: false,
        updateShareApproval: false,
      },
      record: {
        getRecordings: false,
        getRecordingById: false,
        filterClone: false,
        createRecording: false,
      },
      tour: {
        createTour: false,
      },
      share: {
        createShareLink: false,
        getAllActiveShares: false,
        getAllSharesOfEntity: false,
        getFilterOptionsForSharesBySource: false,
        getShareDataBySessionToken: false,
        getShareMetadataByToken: false,
        getSharedEntityDataByToken: false,
        searchAllSharesBySource: false,
        sendVerificationEmailForShareInvitedUser: false,
        toggleShareActiveState: false,
        updateShareLink: false,
      },
    },
  },
  environment: {
    presenter: {
      demo: {
        createADemo: false,
        shareDemo: true,
        updateShareApproval: false,
      },
      record: {
        getRecordings: false,
        getRecordingById: false,
        filterClone: false,
        createRecording: false,
      },
      tour: {
        createTour: false,
      },
      share: {
        createShareLink: true,
        getAllActiveShares: true,
        getAllSharesOfEntity: true,
        getFilterOptionsForSharesBySource: true,
        getShareDataBySessionToken: true,
        getShareMetadataByToken: true,
        getSharedEntityDataByToken: true,
        searchAllSharesBySource: true,
        sendVerificationEmailForShareInvitedUser: true,
        toggleShareActiveState: true,
        updateShareLink: true,
      },
    },
    editor: {
      demo: {
        createADemo: true,
        shareDemo: true,
        updateShareApproval: true,
      },
      record: {
        getRecordings: true,
        getRecordingById: true,
        filterClone: true,
        createRecording: true,
      },
      tour: {
        createTour: true,
      },
      share: {
        createShareLink: true,
        getAllActiveShares: true,
        getAllSharesOfEntity: true,
        getFilterOptionsForSharesBySource: true,
        getShareDataBySessionToken: true,
        getShareMetadataByToken: true,
        getSharedEntityDataByToken: true,
        searchAllSharesBySource: true,
        sendVerificationEmailForShareInvitedUser: true,
        toggleShareActiveState: true,
        updateShareLink: true,
      },
    },
    owner: {
      demo: {
        createADemo: true,
        shareDemo: true,
        updateShareApproval: true,
      },
      record: {
        getRecordings: true,
        getRecordingById: true,
        filterClone: true,
        createRecording: true,
      },
      tour: {
        createTour: true,
      },
      share: {
        createShareLink: true,
        getAllActiveShares: true,
        getAllSharesOfEntity: true,
        getFilterOptionsForSharesBySource: true,
        getShareDataBySessionToken: true,
        getShareMetadataByToken: true,
        getSharedEntityDataByToken: true,
        searchAllSharesBySource: true,
        sendVerificationEmailForShareInvitedUser: true,
        toggleShareActiveState: true,
        updateShareLink: true,
      },
    },
    admin: {
      demo: {
        createADemo: true,
        shareDemo: true,
        updateShareApproval: true,
      },
      record: {
        getRecordings: true,
        getRecordingById: true,
        filterClone: true,
        createRecording: true,
      },
      tour: {
        createTour: true,
      },
      share: {
        createShareLink: true,
        getAllActiveShares: true,
        getAllSharesOfEntity: true,
        getFilterOptionsForSharesBySource: true,
        getShareDataBySessionToken: true,
        getShareMetadataByToken: true,
        getSharedEntityDataByToken: true,
        searchAllSharesBySource: true,
        sendVerificationEmailForShareInvitedUser: true,
        toggleShareActiveState: true,
        updateShareLink: true,
      },
    },
  },
};

export const LEARNING_HUB_ARTICLE_LINKS = {
  'setting-up-demostack-speaker-notes':
    'https://help.demostack.com/articles/setting-up-demostack-speaker-notes',
  'demostack-and-salesforce-integration':
    'https://help.demostack.com/articles/demostack-and-salesforce-integration',
  'demostack-and-hubspot-integration':
    'https://help.demostack.com/articles/demostack-and-hubspot-integration',
  'shared-folders': 'https://help.demostack.com/articles/shared-folders',
};

export const LEARNING_HUB_CONSOLE = 'https://help.demostack.com/en/';
